/**
 * Generic TimeSeriesChart Component
 *
 * A reusable, configuration-driven time-series chart component using ECharts.
 * Supports single and aggregated views, dual Y-axes, derived fields, and consistent Widget integration.
 *
 * @example
 * ```tsx
 * const config: ITimeSeriesChartConfig<MazData> = {
 *   title: 'mAZ Entwicklung',
 *   tag: 'mAZ',
 *   tagColor: 'primary',
 *   xAxisField: 'bm',
 *   yAxes: [
 *     { name: 'Anzahl', position: 'left' },
 *     { name: 'Quote (%)', position: 'right', max: 100, formatter: (v) => `${v}%`, valueType: 'percentage' }
 *   ],
 *   series: [
 *     { name: 'Bestand', dataField: 'bestand', yAxisIndex: 0, chartType: 'line', smooth: true, color: colors.primary }
 *   ],
 *   dataLoader: (ids) => store.loadHistory(ids)
 * };
 *
 * <TimeSeriesChart config={config} agtId={123} />
 * ```
 */

import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { ECharts, EChartsOption, init } from 'echarts';
import { debounce } from 'lodash';
import _ from 'lodash';
import Widget from 'app/components/common/Widget';
import colors from 'assets/scss/colors.module.scss';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Configuration for a horizontal reference/threshold line (ECharts markLine)
 * drawn across the chart on a series' Y-axis (e.g. "Ambitionsniveau")
 */
export interface IMarkLineConfig {
	/** Y value where the horizontal line is drawn (in the owning series' axis units) */
	yValue: number;

	/** Label text shown over the line on the right-hand side */
	label: string;

	/** Line color (default black) */
	color?: string;

	/** Line type */
	lineType?: 'solid' | 'dashed';
}

/**
 * Configuration for a single data series in the chart
 */
export interface ISeriesConfig<T> {
	/** Display name in legend and tooltip */
	name: string;

	/** Field path in data object (supports nested paths via lodash _.get) */
	dataField: keyof T | string;

	/** Which Y-axis to use (0 = left, 1 = right) */
	yAxisIndex: number;

	/** Chart type for this series */
	chartType: 'line' | 'bar';

	/** Series color (from colors.module.scss) */
	color: string;

	/** Line style configuration */
	lineStyle?: {
		width?: number;
		type?: 'solid' | 'dashed';
	};

	/** Whether to smooth line charts (Bezier interpolation) */
	smooth?: boolean;

	/** Size of data point symbols */
	symbolSize?: number;

	/** Stack group (for stacked bars/areas) */
	stack?: string;

	/** Maximum bar width (for bar charts) */
	barMaxWidth?: number | string;

	/** Optional horizontal reference lines drawn on this series' Y-axis */
	markLines?: IMarkLineConfig[];
}

/**
 * Configuration for a Y-axis
 */
export interface IYAxisConfig {
	/** Axis label */
	name: string;

	/** Position of axis */
	position: 'left' | 'right';

	/** Custom formatter for axis labels */
	formatter?: (value: number) => string;

	/** Value type for tooltip formatting */
	valueType?: 'number' | 'percentage' | 'currency';

	/** Maximum value (optional) */
	max?: number;

	/** Minimum value (optional) */
	min?: number;

	/** Show split lines */
	splitLine?: boolean;
}

/**
 * Configuration for derived/calculated fields
 * Allows computing metrics at render time (e.g., penetration rates)
 */
export interface IDerivedField<T> {
	/** Name of the computed field (will be added to data objects) */
	fieldName: string;

	/** Function to calculate the field value from a data point */
	calculate: (dataPoint: T) => number;
}

/**
 * Data loader function type
 * Supports both single entity (agtId) and aggregated (agtIds[]) views
 */
export type DataLoader<T> = (ids: number | number[]) => Promise<T[]>;

/**
 * Complete configuration for a TimeSeriesChart
 */
export interface ITimeSeriesChartConfig<T> {
	/** Chart title */
	title: string;

	/** Optional tag label (shown in Widget action) */
	tag?: string;

	/** Tag color (from colors.module.scss) */
	tagColor?: string;

	/** Chart height in pixels */
	height?: number;

	/** Field to use for X-axis (typically time/date field) */
	xAxisField: keyof T;

	/** X-axis label rotation in degrees */
	xAxisRotation?: number;

	/** X-axis label interval (0 = show all, 'auto' = auto-calculate, n = show every nth label) */
	xAxisLabelInterval?: number | 'auto';

	/** Data series to display */
	series: ISeriesConfig<T>[];

	/** Y-axis configurations (1 or 2 axes) */
	yAxes: IYAxisConfig[];

	/** Optional derived/calculated fields */
	derivedFields?: IDerivedField<T>[];

	/** Function to load chart data */
	dataLoader: DataLoader<T>;

	/** Custom tooltip formatter */
	tooltipFormatter?: (params: any) => string;

	/** Show legend */
	showLegend?: boolean;

	/** Legend position */
	legendPosition?: 'top' | 'bottom';
}

/**
 * Props for the TimeSeriesChart component
 */
export interface ITimeSeriesChartProps<T> {
	/** Chart configuration */
	config: ITimeSeriesChartConfig<T>;

	/** Single entity ID (e.g., agtId) */
	agtId?: number;

	/** Multiple entity IDs for aggregation (e.g., multiple agencies) */
	agtIds?: number[];

	/** Override title (useful for dynamic aggregation labels) */
	titleOverride?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Apply derived field calculations to data array
 */
export function applyDerivedFields<T extends Record<string, any>>(data: T[], derivedFields?: IDerivedField<T>[]): T[] {
	if (!derivedFields || derivedFields.length === 0) {
		return data;
	}

	return data.map((dataPoint) => {
		const enriched = { ...dataPoint };
		derivedFields.forEach((field) => {
			(enriched as any)[field.fieldName] = field.calculate(dataPoint);
		});
		return enriched;
	});
}

/**
 * Build ECharts option from TimeSeriesChart configuration
 */
export function buildChartOption<T extends Record<string, any>>(config: ITimeSeriesChartConfig<T>, enrichedData: T[]): EChartsOption {
	const { xAxisField, series, yAxes, tooltipFormatter, showLegend = true, legendPosition = 'bottom' } = config;

	// Extract x-axis values
	const xAxisData = enrichedData.map((d) => _.get(d, xAxisField as string));

	// Build series configurations
	const seriesConfigs = series.map((s) => {
		const seriesData = enrichedData.map((d) => {
			const value = _.get(d, s.dataField as string);
			// Round to 1 decimal place if it's a number
			return typeof value === 'number' ? Number(value.toFixed(1)) : value;
		});

		return {
			name: s.name,
			type: s.chartType,
			data: seriesData,
			yAxisIndex: s.yAxisIndex,
			smooth: s.smooth ?? false,
			symbolSize: s.symbolSize ?? 6,
			lineStyle: s.lineStyle
				? {
						width: s.lineStyle.width ?? 2,
						type: s.lineStyle.type ?? 'solid',
				  }
				: { width: 2 },
			itemStyle: {
				color: s.color,
			},
			stack: s.stack,
			barMaxWidth: s.barMaxWidth,
			markLine: s.markLines
				? {
						silent: true,
						symbol: 'none',
						data: s.markLines.map((ml) => ({
							yAxis: ml.yValue,
							lineStyle: {
								color: ml.color ?? colors.black,
								type: ml.lineType ?? 'dashed',
								width: 2,
							},
							label: {
								formatter: ml.label,
								position: 'insideEndTop' as const,
								color: ml.color ?? colors.black,
								fontSize: 11,
							},
						})),
				  }
				: undefined,
		};
	});

	// Build Y-axis configurations
	const yAxisConfigs = yAxes.map((axis) => ({
		type: 'value' as const,
		name: axis.name,
		position: axis.position,
		max: axis.max,
		min: axis.min,
		axisLabel: {
			formatter: axis.formatter ? axis.formatter : undefined,
			fontSize: 11,
		},
		splitLine: {
			show: axis.splitLine !== false,
			lineStyle: {
				color: colors.greyLighter,
			},
		},
	}));

	// Default tooltip formatter with explicit valueType handling
	const defaultTooltipFormatter = (params: any) => {
		if (!Array.isArray(params)) return '';

		const xValue = params[0]?.axisValue || '';
		let result = `<strong>${xValue}</strong><br/>`;

		params.forEach((param: any) => {
			const marker = param.marker;
			const seriesName = param.seriesName;
			const value = param.value;

			// Check if this series uses percentage valueType
			const seriesConfig = series.find((s) => s.name === seriesName);
			const isPercentage = seriesConfig && yAxes[seriesConfig.yAxisIndex]?.valueType === 'percentage';

			if (isPercentage) {
				result += `${marker} ${seriesName}: <strong>${value}%</strong><br/>`;
			} else {
				result += `${marker} ${seriesName}: <strong>${value}</strong><br/>`;
			}
		});

		return result;
	};

	const option: EChartsOption = {
		animationDuration: 200,
		color: series.map((s) => s.color),
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'cross',
			},
			formatter: tooltipFormatter || defaultTooltipFormatter,
		},
		legend: {
			show: showLegend,
			data: series.map((s) => s.name).filter((name) => name !== ''),
			[legendPosition]: 0,
			textStyle: {
				fontSize: 12,
			},
		},
		grid: {
			left: 20,
			right: 20,
			top: 50,
			bottom: legendPosition === 'bottom' ? 30 : 20,
			containLabel: true,
		},
		xAxis: {
			type: 'category',
			boundaryGap: series.some((s) => s.chartType === 'bar'),
			data: xAxisData,
			axisLabel: {
				fontSize: 11,
				rotate: config.xAxisRotation ?? 45,
				interval: config.xAxisLabelInterval ?? 'auto',
			},
		},
		yAxis: yAxisConfigs,
		series: seriesConfigs,
	};

	return option;
}

// ============================================================================
// REACT COMPONENT
// ============================================================================

export const TimeSeriesChart = observer(<T extends Record<string, any>>(props: ITimeSeriesChartProps<T>) => {
	const { config, agtId, agtIds, titleOverride } = props;
	const chartRef = useRef<HTMLDivElement>(null);
	const [chartData, setChartData] = useState<T[]>([]);
	const [loading, setLoading] = useState(true);

	// Unified data loading effect
	useEffect(() => {
		// Determine which IDs to load
		const ids = agtIds?.length ? agtIds : agtId;

		// Early return if no valid IDs
		if (!ids || (typeof ids === 'number' && ids === 0)) {
			setLoading(false);
			return;
		}

		setLoading(true);
		config
			.dataLoader(ids)
			.then((data) => {
				setChartData(data);
			})
			.catch((err) => {
				console.error(`Error loading chart data for ${config.title}:`, err);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [config, agtId, agtIds]);

	// Chart rendering effect
	useEffect(() => {
		if (loading || chartData.length === 0 || !chartRef.current) return;

		// Apply derived field calculations
		const enrichedData = applyDerivedFields(chartData, config.derivedFields);

		// Build ECharts option from config
		const option = buildChartOption(config, enrichedData);

		// Initialize chart
		const chartHeight = config.height ?? 400;
		let chart: ECharts | null = null;

		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg', height: chartHeight });
			chart.setOption(option);
		}

		// Re-fit the chart whenever its container changes width - not just on
		// window resize, but also when e.g. the page's vertical scrollbar appears
		// or the menu collapses. ECharts renders a fixed-pixel-width svg, so a
		// stale width causes horizontal overflow.
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);
		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(chartRef.current);

		// Cleanup
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', handleResize);
			handleResize.cancel();
			chart?.dispose();
		};
	}, [chartRef, chartData, loading, config]);

	// Build widget title
	let widgetTitle = titleOverride || config.title;
	if (agtIds && agtIds.length > 1) {
		widgetTitle = `${config.title}`;
	}

	// Build widget action (tag)
	const action = config.tag ? <span className={`tag is-${config.tagColor || 'primary'}`}>{config.tag}</span> : undefined;

	// Loading state
	if (loading) {
		return (
			<Widget title={widgetTitle} disableCollapse={true} action={action} modifier="is-paddingless">
				<div className="pad-1rem">Lade Daten...</div>
			</Widget>
		);
	}

	// Empty state
	if (chartData.length === 0) {
		return (
			<Widget title={widgetTitle} disableCollapse={true} action={action} modifier="is-paddingless">
				<div className="pad-1rem">Keine historischen Daten verfügbar</div>
			</Widget>
		);
	}

	// Chart render
	return (
		<Widget title={widgetTitle} disableCollapse={true} action={action} modifier="is-paddingless">
			<div className="pad-1rem">
				<div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>
			</div>
		</Widget>
	);
});
